/**
 * Global CMD+K command palette (Phase 4 of the Lean & Clean refactor).
 *
 * This component is the escape hatch that makes the lean sidebar from
 * PR 1.2 and the 9-tab patient detail from PR 1.3 defensible: every
 * route, every patient tab (including those demoted to `visibility:
 * 'hidden'`), and a handful of imperative actions are reachable via
 * fuzzy search — so nothing truly goes away, it just moves off the
 * primary surface.
 *
 * The palette reads from four sources (all pure builders in
 * `src/features/command-palette/sources.ts`):
 *
 *   1. Patient search  — live query against the `patients` table,
 *      scoped to the org, limited to the first 50 matches.
 *   2. Routes          — every nav-registered route the user has
 *      access to, including `hidden` via per-org `nav_overrides`.
 *   3. Patient tabs    — every tab in the patient-detail registry
 *      for the currently-open patient (only when on `/patients/:id`).
 *   4. Actions         — verbs like "Neuer Ernährungsplan", gated
 *      by the relevant feature flag.
 *
 * cmdk handles the fuzzy matching itself; we feed it a normalised
 * `value` string via `toCmdkValue(command)` so keywords and hints are
 * searchable alongside the label.
 */

import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';

import { patientsRepo } from '@/data';
import { useAuth } from '@/contexts/AuthContext';
import { useFeatures } from '@/hooks/useFeatures';
import { ROUTES } from '@/features/route-registry';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  buildActionCommands,
  buildPatientCommands,
  buildPatientTabCommands,
  buildRouteCommands,
  extractCurrentPatientId,
  toCmdkValue,
  type Command,
} from '@/features/command-palette/sources';

export const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  // Debounced search query gegen den Server (UX-Audit Phase 3.5). Trennt
  // den "user tippt"-Rhythmus vom Network-Roundtrip: die Anzeige bleibt
  // responsiv, der Server bekommt nicht jeden Tastenanschlag.
  const [rawQuery, setRawQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedQuery(rawQuery.trim()), 220);
    return () => window.clearTimeout(id);
  }, [rawQuery]);
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useAuth();
  const { hasFeature, navOverrides, settingType, isSuperadmin } = useFeatures();
  const orgId = profile?.organization_id;

  // ── Keyboard shortcut — CMD+K / Ctrl+K ────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // ── Patient search (only fires while the palette is open) ────────────
  //
  // Server-side ILIKE-Suche ueber first_name/last_name/email (siehe
  // `listPatients` in `data/patients.ts`). Bei leerem Query laedt die
  // Palette die ersten 50 Patient:innen wie bisher — sobald der User
  // tippt, wird die Liste mit dem debounced-Query refreshed. So findet
  // die Palette auch Namen jenseits der Top-50.
  const { data: patients = [] } = useQuery({
    queryKey: ['patients-search-palette', orgId, debouncedQuery],
    enabled: !!orgId && open,
    queryFn: async () => {
      const rows = await patientsRepo.listPatients(orgId!, {
        limit: 50,
        search: debouncedQuery || undefined,
      });
      return rows.map((p) => ({
        id: p.id,
        first_name: p.first_name,
        last_name: p.last_name,
      }));
    },
    // Server-Antwort kurz im Cache halten, damit Backspace nicht jedes
    // Mal einen neuen Roundtrip ausloest.
    staleTime: 30_000,
  });

  // ── Build the four command lists via pure helpers ───────────────────
  const currentPatientId = useMemo(
    () => extractCurrentPatientId(location.pathname),
    [location.pathname],
  );

  const routeCommands = useMemo(
    () =>
      buildRouteCommands(ROUTES, {
        role: profile?.role,
        isSuperadmin,
        hasFeature,
        navOverrides,
      }),
    [profile?.role, isSuperadmin, hasFeature, navOverrides],
  );

  const patientTabCommands = useMemo(
    () =>
      buildPatientTabCommands(currentPatientId, {
        hasFeature,
        // Inpatient status is patient-specific — the palette doesn't
        // load the patient row, so we lean on `settingType === 'klinik'`
        // to surface klinik tabs. Mis-classifying here is harmless:
        // clicking the command lands on the patient page which does
        // its own gating.
        isInpatient: settingType === 'klinik',
        settingType,
        isSuperadmin,
      }),
    [currentPatientId, hasFeature, settingType, isSuperadmin],
  );

  const actionCommands = useMemo(
    () => buildActionCommands({ hasFeature, currentPatientId }),
    [hasFeature, currentPatientId],
  );

  const patientCommands = useMemo(
    () => buildPatientCommands(patients),
    [patients],
  );

  // Assemble + group for render. Keep the order consistent between
  // renders — cmdk re-evaluates filtering on every change, a stable
  // group order prevents "commands jumping around while typing".
  const groups = useMemo(() => {
    return [
      { heading: 'Patienten', items: patientCommands },
      { heading: 'Patientenakte', items: patientTabCommands },
      { heading: 'Aktionen', items: actionCommands },
      {
        heading: 'Navigation',
        items: routeCommands.filter((c) => c.group === 'Navigation'),
      },
      {
        heading: 'Administration',
        items: routeCommands.filter((c) => c.group === 'Administration'),
      },
    ].filter((g) => g.items.length > 0);
  }, [patientCommands, patientTabCommands, actionCommands, routeCommands]);

  const runCommand = (command: Command) => {
    setOpen(false);
    if (command.href) navigate(command.href);
    else command.onRun?.();
  };

  return (
    <>
      {/* Subtle search hint in the sidebar — clicking opens the palette.
          `data-tour` anchors the Phase 5B onboarding tour's "CMD+K" step. */}
      <button
        onClick={() => setOpen(true)}
        data-tour="command-palette-hint"
        className="flex items-center gap-2 px-3 py-2 mx-3 mb-2 rounded-xl text-xs text-muted-foreground bg-secondary/60 hover:bg-secondary transition-colors"
        aria-label="Suche öffnen"
      >
        <Search className="w-3.5 h-3.5" />
        <span className="flex-1 text-left">Suche…</span>
        <kbd className="text-[10px] bg-background border border-border rounded px-1.5 py-0.5 font-mono">
          {navigator.platform.includes('Mac') ? '\u2318' : 'Ctrl'}+K
        </kbd>
      </button>

      <CommandDialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          // Beim Schliessen den Query zuruecksetzen, damit die naechste
          // Oeffnung mit der initialen Patient-Top-50 startet statt mit
          // dem vorherigen Filter-Set.
          if (!next) {
            setRawQuery('');
            setDebouncedQuery('');
          }
        }}
      >
        <CommandInput
          placeholder="Patient, Seite, Aktion …"
          value={rawQuery}
          onValueChange={setRawQuery}
        />
        <CommandList>
          <CommandEmpty>Keine Ergebnisse gefunden.</CommandEmpty>
          {groups.map((group, idx) => (
            <>
              {idx > 0 && <CommandSeparator key={`sep:${group.heading}`} />}
              <CommandGroup key={group.heading} heading={group.heading}>
                {group.items.map((command) => {
                  const Icon = command.icon;
                  return (
                    <CommandItem
                      key={command.id}
                      value={toCmdkValue(command)}
                      onSelect={() => runCommand(command)}
                      className="cursor-pointer"
                    >
                      <Icon className="w-4 h-4 mr-2 text-muted-foreground shrink-0" />
                      <span className="flex-1 truncate">{command.label}</span>
                      {command.hint && (
                        <span className="text-[10px] text-muted-foreground/70 ml-2 shrink-0">
                          {command.hint}
                        </span>
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </>
          ))}
        </CommandList>
      </CommandDialog>
    </>
  );
};
